import Controller from '@ember/controller';
import { all } from 'rsvp';

import { task, timeout } from 'ember-concurrency';
import { inject as controller } from '@ember/controller';
import { inject as service } from '@ember/service';
import { action, get, set } from '@ember/object';
import { tracked } from '@glimmer/tracking';

import { clearAttribute } from 'beebee-base/lib/utility';
import { beebeeAjaxGet } from 'beebee-base/lib/utility/ajax';

import { paesiMondoAlfa3, paesiMondoAlfa2 } from 'beebee-base/lib/list_taxes';

import getConfig from 'beebee-base/lib/get-config';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      controller — standalone deep-link page for a `/payment_id`+`/amount`
 *              payment request sent to a contact (e.g. via `sync_relations`/
 *              request_payment from checkout.js). Loads the payment, lets the contact
 *              complete/confirm their fiscal data, and offers a retry-payment path.
 *   extends:   Controller (@ember/controller)
 *   template:  none
 *   qp:        payment_id, amount
 *   key:       initPageTask (authenticates from the deep-link token, kicks
 *              loadPaymentTask + applicationController.loadMe), loadPaymentTask
 *              (/payments/:id), retryPaymentTask (clones a refused payment into a new
 *              draft with the same projects/events/contacts), saveContactTask (fiscal
 *              save via applicationController.saveRecordRelated), checkRequired/
 *              completaStraniero (fiscal validation — near-duplicate of checkout.js's).
 *   uses:      applicationController (@controller('application') — gdprField, loadMe,
 *              saveRecordRelated, sendInvitation), beebee-base/lib/list_taxes
 *              (paesiMondoAlfa2/3), lib/utility/ajax, lib/get-config
 *   siblings:  routes/payment-request.js (kicks initPageTask on entry, cancels on
 *              leave), checkout.js (near-duplicate checkRequired/completaStraniero)
 *   tests:     payment-request-canbuy-test.js, payment-request-tasks-test.js
 *   gotchas:   retryPaymentTask and saveContactTask both call
 *              applicationController.saveRecordRelated, which never rejects — it
 *              resolves `false` on a refused save. Both check `=== false` explicitly
 *              and roll back / bail before doing anything that assumes the save
 *              succeeded (retryPaymentTask rolls back the draft payment;
 *              saveContactTask skips the reloadMeTask that would otherwise overwrite
 *              the just-typed fiscal data with stale server state).
 *
 * Deep-linked payment-completion page for a contact who received a payment
 * request outside the normal checkout flow.
 */
export default class PaymentRequestController extends Controller {
  @controller('application') applicationController;

  @service session;

  @service notify;
  @service store;
  @service intl;
  @service log;

  queryParams = ['payment_id', 'amount'];

  @tracked payment_id;
  @tracked amount;

  @tracked reloadCheck = false;
  @tracked checkTerms = false;

  @tracked termAccepted;
  @tracked dataAvailable;
  @tracked payment;
  @tracked emptyContactData;

  get not_required_field() {
    return this.session.option_field?.ecommerce_not_required_field || [];
  }

  get generalTerms() {
    return this.session.option_field?.ecommerce_general_term || null;
  }

  get canBuy() {
    if (!this.generalTerms) {
      return true;
    }

    if (!this.termAccepted) {
      return false;
    }

    let requiredActive = true;
    const termOptions = this.applicationController.get('gdprField.organization_term_options.data');

    if (Array.isArray(termOptions)) {
      termOptions.forEach((d) => {
        // Same invalidation contract as checkout.js `canBuy` — the consent
        // rows are plain objects written by `(mut c.active)` (= `set()`), so
        // the read has to go through `get` for this getter to recompute when
        // the customer ticks a required consent. See checkout.js for the full
        // mechanism.
        if (get(d, 'required') && get(d, 'registration') && !get(d, 'active')) {
          requiredActive = false;
        }
      });
    }

    return requiredActive;
  }

  get pagamentoVerificato() {
    const paymentMethod = this.content?.payment_method;
    const transactionType = this.content?.transaction_type;

    return paymentMethod === 'Bonifico' ? false : !!transactionType;
  }

  @task({ drop: true }) *initPageTask() {
    if (!this.session.token || !this.payment_id) {
      return;
    }

    this.session.authenticate(this.session.token, null, false);
    yield timeout(150);
    this.loadPaymentTask.perform();
    this.applicationController.loadMe.perform();
  }

  @task({ drop: true }) *loadPaymentTask() {
    var self = this;

    set(this, 'dataAvailable', false);

    try {
      const payment = yield beebeeAjaxGet(
        `${getConfig(this).APP.urlApi}/payments/${this.payment_id}`,
        self.session.token
      );

      self.store.pushPayload(payment);
      const paymentStore = self.store.peekRecord('payment', this.payment_id);

      set(this, 'payment', paymentStore);

      set(this, 'dataAvailable', true);
      this.checkEmptyContact();
    } catch (e) {
      this.log.error(e);
      this.notify.error(this.intl.t('beebeeboard error'));
    }
  }

  checkEmptyContact() {
    let checkField = false;

    if (this.session.me?.id) {
      checkField = this.checkRequired(false);
    }

    set(this, 'emptyContactData', checkField);
  }

  @task({ drop: true }) *retryPaymentTask() {
    let newPayment = null;
    try {
      newPayment = this.store.createRecord('payment', {
        sent: false,
        date: this.payment.get('date'),
        total: this.payment.get('total'),
        option_json: this.payment.get('option_json'),
        transaction_type: this.payment.get('transaction_type'),
        success: false,
      });

      // Assicurati che contacts, projects ed events siano caricati asincronamente anche per newPayment
      const [projects, events, contacts] = yield all([
        this.payment.get('projects'),
        this.payment.get('events'),
        this.payment.get('contacts'),
      ]);

      const [newProjects, newEvents, newContacts] = yield all([
        newPayment.get('projects'),
        newPayment.get('events'),
        newPayment.get('contacts'),
      ]);

      for (const i of (projects || []).slice()) {
        newProjects.push(i);
      }

      for (const i of (events || []).slice()) {
        newEvents.push(i);
      }

      for (const i of (contacts || []).slice()) {
        newContacts.push(i);
      }

      const item = yield this.applicationController.saveRecordRelated(newPayment);
      //this.store.pushPayload(item[0]);

      // saveRecordRelated NEVER rejects — it resolves `false` on failure, so the catch
      // below never fires for a refused save and its rollback never ran: the unsaved
      // retry `payment` stayed in the store as an isNew record, ready to be swept into
      // a later related save.
      if (item === false) {
        if (!newPayment.isDestroyed && !newPayment.isDestroying) {
          newPayment.rollbackAttributes();
        }
        return;
      }

      if (item) {
        const savedId = item[0]?.data?.id;
        if (savedId) {
          const paymentSaved = this.store.peekRecord('payment', savedId);
          set(this, 'payment', paymentSaved);
        }
      }
    } catch (error) {
      // Roll back the unsaved retry payment so it doesn't orphan in the store.
      if (newPayment && !newPayment.isDestroyed && !newPayment.isDestroying) {
        newPayment.rollbackAttributes();
      }
      this.log.error('Error retrying payment:', error);
      this.notify.error(this.intl.t('beebeeboard error'));
    }
  }

  @task({ drop: true }) *saveContactTask() {
    const meId = this.session.me?.id;
    const contact = this.store.peekRecord('contact', meId);

    if (this.checkRequired()) {
      // Inverti il valore di 'my_dirty'
      set(contact, 'my_dirty', !contact.get('my_dirty'));

      // Salva il record associato.
      // saveRecordRelated NEVER rejects — it resolves `false` on failure. reloadMeTask
      // below refetches `me` from the server, so running it after a refused save
      // overwrote the billing/fiscal data the customer had just typed with stale
      // server state. Strictly `=== false`: that is the only refusal signal.
      const saved = yield this.applicationController.saveRecordRelated(contact);
      if (saved === false) {
        return;
      }

      // Ricarica il contatto 'me' e aggiorna i dati nel controller
      yield this.session.reloadMeTask.perform();

      // Controlla se il contatto è vuoto
      this.checkEmptyContact();

      // Scorri la pagina verso l'alto
      window.scrollTo({ top: 100, behavior: 'smooth' });
    } else {
      this.notify.warning(this.intl.t('missing mandatory fields'));
    }
  }

  checkRequired(showError = true) {
    const contact = this.store.peekRecord('contact', this.session.me?.id);
    const fieldReset = [
      'nameError',
      'ragSocialeError',
      'birthError',
      'luogoError',
      'mobileError',
      'mailError',
      'cfError',
      'codfiscaleError',
      'addressError',
    ];
    const fieldTrim = [
      'ragione_sociale',
      'first_name',
      'last_name',
      'cod_fiscale',
      'luogo_nascita',
      'mail',
      'mobile',
    ];

    // Reset error fields if showError is true
    if (showError) {
      fieldReset.forEach((field) => {
        set(contact, field, false);
      });
    }

    // Trim fields
    fieldTrim.forEach((field) => {
      const value = clearAttribute(contact.get(field));
      set(contact, field, value);
    });

    let checkField = true;
    const notRequired = this.not_required_field || {};
    const isFisicaGiuridica = contact.get('fisica_giuridica');
    const address = contact.get('addresses')[0];
    const country = address?.location_address_country;
    const countryCode = address?.location_address_country_code;

    // Check for 'ragione sociale' or name fields
    if (isFisicaGiuridica) {
      if (!contact.get('ragione_sociale')) {
        if (showError) {
          set(contact, 'ragSocialeError', true);
        }
        checkField = false;
      }
    } else {
      if (!contact.get('first_name') || !contact.get('last_name')) {
        if (showError) {
          set(contact, 'nameError', true);
        }
        checkField = false;
      }
    }

    // Check birth and place of birth fields
    if (!contact.get('data_nascita') && !notRequired.data_nascita) {
      if (showError) {
        set(contact, 'birthError', true);
      }
      checkField = false;
    }
    if (!contact.get('luogo_nascita') && !notRequired.luogo_nascita) {
      if (showError) {
        set(contact, 'luogoError', true);
      }
      checkField = false;
    }

    // Check email and mobile
    if (!contact.get('mail')) {
      if (showError) {
        set(contact, 'mailError', true);
      }
      checkField = false;
    }

    if (!contact.get('mobile') && !notRequired.mobile) {
      if (showError) {
        set(contact, 'mobileError', true);
      }
      checkField = false;
    }

    // Check address fields
    if (
      !(
        address?.location_address_street &&
        address?.location_address_house &&
        address?.location_address_postal_code &&
        address?.location_address_city &&
        address?.location_address_country
      ) &&
      !notRequired.address
    ) {
      if (showError) {
        set(contact, 'addressError', true);
      }
      checkField = false;
    }

    // Check Codice Fiscale for Italian addresses
    const isItaly = country === 'Italia' || ['IT', 'ITA'].includes(countryCode);
    if (isItaly) {
      // completaStraniero() must never run for Italian contacts: it would
      // overwrite the real postal code / fiscal data with placeholder values.
      if (
        !notRequired.cod_fiscale &&
        (!contact.get('cod_fiscale') || contact.get('codFiscaleValidMsg'))
      ) {
        if (showError) {
          set(contact, 'cfError', true);
        }
        checkField = false;
      }
    } else {
      this.completaStraniero();
    }

    return checkField;
  }

  completaStraniero() {
    const item = this.store.peekRecord('contact', this.session.me?.id);
    if (!item) return;
    const contactAddress = item.addresses?.[0];
    if (!contactAddress) return;
    const countryCode = contactAddress.location_address_country_code;
    const country = contactAddress.location_address_country;
    const isItaly = country === 'Italia' || ['IT', 'ITA'].includes(countryCode);
    const contactId = item.id || '';

    if (isItaly || !countryCode) {
      return;
    }

    const convertCountryCodeToAlpha2 = (countryCode) => {
      const index = paesiMondoAlfa3.indexOf(countryCode);

      if (index === -1) {
        return countryCode.substring(0, 2);
      }

      return paesiMondoAlfa2[index];
    };

    const alpha2Code = convertCountryCodeToAlpha2(countryCode);

    set(contactAddress, 'location_address_postal_code', '00000');
    set(item, 'codice_destinatario', 'XXXXXXX');

    if (item.fisica_giuridica) {
      const rimanentiCF = 14 - contactId.length;
      const contactCF = `${alpha2Code}${'00000000000000'.substring(0, rimanentiCF)}${contactId}`;
      set(item, 'cod_fiscale', contactCF);
    } else {
      set(item, 'cod_fiscale', null);
    }

    const rimanentiPIVA = 11 - contactId.length;
    const pIVA = `${alpha2Code}${'999999999'.substring(0, rimanentiPIVA)}${contactId}`;
    set(item, 'piva', pIVA);
  }

  @action
  sendInvitation() {
    this.applicationController.sendInvitation.perform();
  }
}
